import React, { useState } from 'react';
import { useI18n } from './i18n';

/**
 * Mercadini/AIS pairing graphic sheet (the official "pairing graphic sheet").
 * Faithful reproduction of page 47 of the AIS PDF (Renzo Dutto, 2011).
 *
 * FULL CIRCLE with 10 concentric circles (scale 1-10).
 *
 * FOOD parameters are rays from the centre (no checkbox).
 *
 * 9 WINE parameters on PARALLEL LINES (from the 1st circle, each ending in a square box to tick):
 *   - left lines:  box TEXT (box towards the circle, text outside)
 *   - right lines: TEXT box (text outside, box towards the circle)
 *
 *   Top-left (-25°):     SWEETNESS / SOFTNESS
 *   Top-right (+25°):    OLFACTORY INTENSITY / PAI
 *   Bottom-left (-125°): ALCOHOL / TANNICITY
 *   Bottom-right (+125°): ACIDITY / EFFERVESCENCE + SAPIDITY (same line)
 */

const W = 580;
const H = 780;
const CX = W / 2;
const CY = 310;
const R_MAX = 185;
const R_MIN = R_MAX / 10;
const STEPS = 10;
const GAP = 12;

// A parameter is filled in when it holds a number (0 included): null / undefined / '' are "not filled in" and are never
// plotted nor counted as 0.
const isFilledVal = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));

function pt(angleDeg, radius) {
  const a = angleDeg * (Math.PI / 180);
  return { x: CX + radius * Math.sin(a), y: CY - radius * Math.cos(a) };
}

function ppt(angleDeg, offset, radius) {
  const a = angleDeg * (Math.PI / 180);
  const t = Math.sqrt(Math.max(0, radius * radius - offset * offset));
  return {
    x: CX + t * Math.sin(a) + offset * Math.cos(a),
    y: CY - t * Math.cos(a) + offset * Math.sin(a),
  };
}

const ALL_LINES = [
  // ── FOOD — rays from the centre ──
  { key: 'food_sweet_tendency', type: 'ray', angle: -60,  side: 'left',  isWine: false },
  { key: 'food_fatness',      type: 'ray', angle: -80,  side: 'left',  isWine: false },
  { key: 'food_succulence',     type: 'ray', angle: 60,   side: 'right', isWine: false },
  { key: 'food_unctuousness',      type: 'ray', angle: 80,   side: 'right', isWine: false },
  // Bottom-left concordance group: sapidity, bitter tendency, acid tendency, sweetness (same ray)
  { key: 'food_sapidity',              type: 'ray', angle: -155, side: 'left',  isWine: false },
  { key: 'food_bitter_tendency',   type: 'ray', angle: -155, side: 'left',  isWine: false },
  { key: 'food_acid_tendency',        type: 'ray', angle: -155, side: 'left',  isWine: false },
  { key: 'food_sweetness',              type: 'ray', angle: -155, side: 'left',  isWine: false },
  // Bottom-right concordance group: persistence, spiciness, aromaticity (same ray)
  { key: 'food_persistence', type: 'ray', angle: 155,  side: 'right', isWine: false },
  { key: 'food_spiciness',                  type: 'ray', angle: 155,  side: 'right', isWine: false },
  { key: 'food_aromaticity',                 type: 'ray', angle: 155,  side: 'right', isWine: false },

  // ── WINE — PARALLEL lines from the 1st circle, each ending in a box ──
  // Left vertical line (angle 0°, offset -GAP): sweetness + softness (same line)
  { key: 'wine_sweetness',            type: 'parallel', angle: 0,  offset: -GAP, side: 'left',  isWine: true },
  { key: 'wine_softness',          type: 'parallel', angle: 0,  offset: -GAP, side: 'left',  isWine: true },
  // Right vertical line (angle 0°, offset +GAP): olfactory intensity + PAI (same line)
  { key: 'wine_olfactory_intensity', type: 'parallel', angle: 0,  offset: GAP,  side: 'right', isWine: true },
  { key: 'wine_pai',                 type: 'parallel', angle: 0,  offset: GAP,  side: 'right', isWine: true },
  // Bottom-left: alcohol + tannicity
  { key: 'wine_alcohol',    type: 'parallel', angle: -125, offset: GAP,  side: 'left',  isWine: true },
  { key: 'wine_tannicity',     type: 'parallel', angle: -125, offset: -GAP, side: 'left',  isWine: true },
  // Bottom-right: acidity + effervescence/sapidity (same line)
  { key: 'wine_acidity',       type: 'parallel', angle: 125,  offset: -GAP, side: 'right', isWine: true },
  { key: 'wine_effervescence', type: 'parallel', angle: 125,  offset: GAP,  side: 'right', isWine: true },
  { key: 'wine_sapidity',      type: 'parallel', angle: 125,  offset: GAP,  side: 'right', isWine: true },
];

// ── Polygons: one point per ray/group, using the group's maximum value ──

// FOOD: 4 rays + 2 concordance groups (below the circle)
// Left group: sapidity, bitter tendency, acid tendency, sweetness → direction ~-155° (bottom-left)
// Right group: persistence, spiciness, aromaticity → direction ~+155° (bottom-right)
// Clockwise order: fatness → sweet tendency → succulence → unctuousness → persistence group → sapidity group
// so that the polygon has no crossing edges
const FOOD_POLY = [
  { type: 'ray',   keys: ['food_fatness'],       angle: -80 },
  { type: 'ray',   keys: ['food_sweet_tendency'],  angle: -60 },
  { type: 'ray',   keys: ['food_succulence'],      angle: 60 },
  { type: 'ray',   keys: ['food_unctuousness'],       angle: 80 },
  { type: 'ray',   keys: ['food_persistence','food_spiciness','food_aromaticity'],
                    angle: 155 },
  { type: 'ray',   keys: ['food_sapidity','food_bitter_tendency','food_acid_tendency','food_sweetness'],
                    angle: -155 },
];

// WINE: one point per pair of parallel lines (maximum value)
const WINE_POLY = [
  { keys: ['wine_sweetness','wine_softness'],     angle: 0,    offset: -GAP },
  { keys: ['wine_olfactory_intensity','wine_pai'],  angle: 0,    offset: GAP },
  { keys: ['wine_acidity'],                          angle: 125,  offset: -GAP },
  { keys: ['wine_effervescence','wine_sapidity'],   angle: 125,  offset: GAP },
  { keys: ['wine_tannicity'],                        angle: -125, offset: -GAP },
  { keys: ['wine_alcohol'],                       angle: -125, offset: GAP },
];

function getPoint(line, val) {
  const r = (val / STEPS) * R_MAX;
  if (line.type === 'parallel') {
    return ppt(line.angle, line.offset, Math.max(r, Math.abs(line.offset) + 1));
  }
  return pt(line.angle, r);
}

function getLineEndpoints(line) {
  if (line.type === 'parallel') {
    // The line must end exactly at the box, not beyond it
    const extra = 33; // same as the getLabelPoint extra minus a 2px margin
    return {
      start: ppt(line.angle, line.offset, R_MIN),
      end: ppt(line.angle, line.offset, R_MAX + extra),
    };
  }
  return { start: { x: CX, y: CY }, end: pt(line.angle, R_MAX + 3) };
}

function getLabelPoint(line) {
  if (line.type === 'parallel') {
    // Use a point along the line direction, well beyond the outer circle: it must be CLEARLY outside.
    // Vertical lines (0°): add distance along the axis.
    // Oblique lines (-125°, +125°): more distance is needed because the perpendicular offset
    //   reduces the effective distance from the circle.
    const extra = 35;
    return ppt(line.angle, line.offset, R_MAX + extra);
  }
  return pt(line.angle, R_MAX + 18);
}

function RadarChart({ data }) {
  const { t, tc, lang } = useI18n();
  const [hover, setHover] = useState(null);      // { side: 'food'|'wine', index } of the plotted vertex under the pointer
  if (!data) data = {};

  // Shrinks the font when a translation does not fit the available space (viewBox px)
  const fit = (txt, avail, base, k) => Math.max(6.5, Math.min(base, avail / (String(txt).length * k)));
  const up = (txt) => String(txt).toLocaleUpperCase(lang);
  const lineLabel = (line) => {
    const txt = t(`pairing.chart.${line.key}`);
    return line.isWine ? up(txt) : txt;
  };

  // ══ 10 concentric circles ══
  const circles = [];
  for (let i = 1; i <= STEPS; i++) {
    const r = (i / STEPS) * R_MAX;
    circles.push(
      <circle key={`c-${i}`} cx={CX} cy={CY} r={r}
        fill="none" stroke={i === STEPS ? '#999' : '#ccc'}
        strokeWidth={i === STEPS ? 1.2 : 0.5} />
    );
  }

  // ══ Scale numbers — between each pair of parallel lines ══
  // Directions of the 3 pairs: 0° (vertical, top), -125° (bottom-left), +125° (bottom-right)
  const pairDirs = [0, -125, 125];
  const scaleNums = [];
  for (const dir of pairDirs) {
    for (let i = 1; i <= STEPS; i++) {
      const r = (i / STEPS) * R_MAX;
      // Point midway between the two parallel lines (offset = 0)
      const p = pt(dir, r);
      scaleNums.push(
        <text key={`sn-${dir}-${i}`} x={p.x} y={p.y + 3.5}
          textAnchor="middle" fontSize="9" fill="#555" fontWeight="800">{i}</text>
      );
    }
  }

  // ══ Draw lines (skip duplicates for shared lines) ══
  const drawnIds = new Set();
  const lineEls = ALL_LINES.map(line => {
    const lid = line.type === 'parallel' ? `p_${line.angle}_${line.offset}` : `r_${line.angle}`;
    if (drawnIds.has(lid)) return null;
    drawnIds.add(lid);
    const { start, end } = getLineEndpoints(line);
    return <line key={`ln-${line.key}`} x1={start.x} y1={start.y} x2={end.x} y2={end.y}
      stroke="#bbb" strokeWidth={0.7} />;
  }).filter(Boolean);

  // ══ Labels + checkboxes ══
  // The bottom-left and bottom-right parallel pairs use a FIXED X so that the boxes line up vertically.
  //
  // Bottom-left (ALCOHOL, TANNICITY): boxes aligned on the RIGHT, text on the left
  // Bottom-right (ACIDITY, EFFERVESCENCE, SAPIDITY): boxes aligned on the LEFT, text on the right
  // Top-left/right and rays: positioned relative to the point on the line

  // Fixed X for the bottom groups, just outside the last circle
  const bottomLeftBoxX = 95;       // box X for the bottom-left group
  const bottomRightBoxX = W - 105; // box X for the bottom-right group

  const labelStack = {};
  const labelEls = ALL_LINES.map(line => {
    const lid = line.type === 'parallel' ? `p_${line.angle}_${line.offset}` : `r_${line.angle}`;
    const idx = labelStack[lid] || 0;
    labelStack[lid] = idx + 1;

    const color = line.isWine ? '#722f37' : '#b35900';
    const fontW = line.isWine ? '700' : '500';
    const basePt = getLabelPoint(line);
    const ly = basePt.y + idx * 18;
    const filled = isFilledVal(data[line.key]);
    const val = filled ? Number(data[line.key]) : null;
    const label = lineLabel(line);
    const kk = line.isWine ? 0.72 : 0.58;

    if (line.type === 'parallel') {
      const isBottom = Math.abs(line.angle) > 90;

      if (isBottom && line.side === 'left') {
        // Bottom-left (ALCOHOL, TANNICITY): TEXT box — right-aligned with a fixed box
        return (
          <g key={`lb-${line.key}`}>
            <rect x={bottomLeftBoxX} y={ly - 5.5} width={11} height={11}
              fill={filled ? color : 'white'} stroke={color} strokeWidth={0.8} rx={1} />
            {filled && <text x={bottomLeftBoxX + 5.5} y={ly + 3.5} textAnchor="middle" fontSize="7" fill="white" fontWeight="bold">{val}</text>}
            {!filled && <text x={bottomLeftBoxX + 5.5} y={ly + 3.5} textAnchor="middle" fontSize="8" fill="#999">–</text>}
            <text x={bottomLeftBoxX - 4} y={ly + 3.5}
              textAnchor="end" fontSize={fit(label, bottomLeftBoxX - 7, 10, kk)} fill={color} fontWeight={fontW}>
              {label}
            </text>
          </g>
        );
      } else if (isBottom && line.side === 'right') {
        // Bottom-right (ACIDITY, EFFERVESCENCE, SAPIDITY): box TEXT — left-aligned with a fixed box
        return (
          <g key={`lb-${line.key}`}>
            <rect x={bottomRightBoxX} y={ly - 5.5} width={11} height={11}
              fill={filled ? color : 'white'} stroke={color} strokeWidth={0.8} rx={1} />
            {filled && <text x={bottomRightBoxX + 5.5} y={ly + 3.5} textAnchor="middle" fontSize="7" fill="white" fontWeight="bold">{val}</text>}
            {!filled && <text x={bottomRightBoxX + 5.5} y={ly + 3.5} textAnchor="middle" fontSize="8" fill="#999">–</text>}
            <text x={bottomRightBoxX + 15} y={ly + 3.5}
              textAnchor="start" fontSize={fit(label, W - (bottomRightBoxX + 15) - 3, 10, kk)} fill={color} fontWeight={fontW}>
              {label}
            </text>
          </g>
        );
      } else if (line.side === 'left') {
        // Top-left (SWEETNESS, SOFTNESS): box TEXT
        const lx = basePt.x;
        return (
          <g key={`lb-${line.key}`}>
            <rect x={lx - 1} y={ly - 5.5} width={11} height={11}
              fill={filled ? color : 'white'} stroke={color} strokeWidth={0.8} rx={1} />
            {filled && <text x={lx + 4.5} y={ly + 3.5} textAnchor="middle" fontSize="7" fill="white" fontWeight="bold">{val}</text>}
            {!filled && <text x={lx + 4.5} y={ly + 3.5} textAnchor="middle" fontSize="8" fill="#999">–</text>}
            <text x={lx - 5} y={ly + 3.5}
              textAnchor="end" fontSize={fit(label, lx - 8, 10, kk)} fill={color} fontWeight={fontW}>
              {label}
            </text>
          </g>
        );
      } else {
        // Top-right (OLFACTORY INTENSITY, PAI): TEXT box
        const lx = basePt.x;
        return (
          <g key={`lb-${line.key}`}>
            <rect x={lx - 12} y={ly - 5.5} width={11} height={11}
              fill={filled ? color : 'white'} stroke={color} strokeWidth={0.8} rx={1} />
            {filled && <text x={lx - 6.5} y={ly + 3.5} textAnchor="middle" fontSize="7" fill="white" fontWeight="bold">{val}</text>}
            {!filled && <text x={lx - 6.5} y={ly + 3.5} textAnchor="middle" fontSize="8" fill="#999">–</text>}
            <text x={lx + 3} y={ly + 3.5}
              textAnchor="start" fontSize={fit(label, W - lx - 6, 10, kk)} fill={color} fontWeight={fontW}>
              {label}
            </text>
          </g>
        );
      }
    } else {
      // Food ray → text + value when > 0
      const lx = basePt.x;
      if (line.side === 'left') {
        return (
          <g key={`lb-${line.key}`}>
            <text x={lx - 4} y={ly + 3.5}
              textAnchor="end" fontSize={fit(label, lx - 7, 10, kk)} fill={color} fontWeight={fontW}>
              {label}
            </text>
            <text x={lx + 6} y={ly + 3.5} textAnchor="start" fontSize="9" fill={filled ? color : '#999'} fontWeight="700">{filled ? val : '—'}</text>
          </g>
        );
      }
      return (
        <g key={`lb-${line.key}`}>
          <text x={lx + 4} y={ly + 3.5}
            textAnchor="start" fontSize={fit(label, W - lx - 7, 10, kk)} fill={color} fontWeight={fontW}>
            {label}
          </text>
          <text x={lx - 6} y={ly + 3.5} textAnchor="end" fontSize="9" fill={filled ? color : '#999'} fontWeight="700">{filled ? val : '—'}</text>
        </g>
      );
    }
  });

  // ══ FOOD polygon — only groups with at least one filled-in parameter (not filled in = not plotted) ══
  const filledMax = (keys) => {
    const vals = keys.map(k => data[k]).filter(isFilledVal).map(Number);
    return vals.length ? Math.max(...vals) : null;
  };
  const foodActive = FOOD_POLY.map(g => {
    const maxVal = filledMax(g.keys);
    return maxVal === null ? null : { maxVal, keys: g.keys, point: pt(g.angle, (maxVal / STEPS) * R_MAX) };
  }).filter(Boolean);
  const foodPoly = foodActive.map(x => `${x.point.x},${x.point.y}`).join(' ');

  // ══ WINE polygon — only groups with at least one filled-in parameter ══
  const wineActive = WINE_POLY.map(g => {
    const maxVal = filledMax(g.keys);
    if (maxVal === null) return null;
    const r = (maxVal / STEPS) * R_MAX;
    return { maxVal, keys: g.keys, point: ppt(g.angle, g.offset, Math.max(r, Math.abs(g.offset) + 1)) };
  }).filter(Boolean);
  const winePoly = wineActive.map(x => `${x.point.x},${x.point.y}`).join(' ');

  // Tooltip text of a vertex: the point shows the HIGHEST value of its group of parameters, so every parameter of the group is listed
  const pointLines = (x) => [
    t('pairing.chart.hover_value', { value: x.maxVal }),
    ...x.keys.map((k) => `${t(`pairing.p.${k}`)}: ${isFilledVal(data[k]) ? data[k] : '—'}`),
  ];
  let hoverBox = null;
  if (hover) {
    const x = (hover.side === 'food' ? foodActive : wineActive)[hover.index];
    if (x) {
      const lines = pointLines(x);
      const w = Math.min(W - 8, Math.max(...lines.map((l) => l.length)) * 5.6 + 16);
      const h = lines.length * 13 + 10;
      const bx = Math.max(4, Math.min(W - w - 4, x.point.x + 10));
      const by = Math.max(4, Math.min(H - h - 4, x.point.y - h - 10));
      hoverBox = (
        <g pointerEvents="none" className="mercadini-hover">
          <rect x={bx} y={by} width={w} height={h} rx={4} fill="#fffdf8" stroke={hover.side === 'food' ? '#d4760a' : '#722f37'} strokeWidth={1.2} opacity={0.98} />
          {lines.map((l, i) => (
            <text key={i} x={bx + 8} y={by + 15 + i * 13} fontSize={9.5} fill="#222" fontWeight={i === 0 ? '700' : '400'}>{l}</text>
          ))}
        </g>
      );
    }
  }

  return (
    <div className="mercadini-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="mercadini-svg">
        <rect x={0} y={0} width={W} height={H} fill="white" rx={4} stroke="#ddd" strokeWidth={1} />

        <text x={CX} y={20} textAnchor="middle" fontSize={fit(t('pairing.chart.title_org'), W - 30, 9.5, 0.58)} fill="#333" fontWeight="600">{t('pairing.chart.title_org')}</text>
        <text x={CX} y={34} textAnchor="middle" fontSize={fit(up(t('pairing.chart.title_sub')), W - 30, 9.5, 0.7)} fill="#333" fontWeight="600">{up(t('pairing.chart.title_sub'))}</text>
        <text x={CX} y={50} textAnchor="middle" fontSize={fit(up(t('pairing.chart.title_main')), W - 30, 12, 0.72)} fill="#000" fontWeight="800">{up(t('pairing.chart.title_main'))}</text>

        {circles}
        <line x1={CX} y1={CY - R_MAX - 3} x2={CX} y2={CY + R_MAX + 3} stroke="#bbb" strokeWidth={0.5} />
        {scaleNums}
        {lineEls}
        {labelEls}

        {/* FOOD polygon — drawn only with at least 2 active vertices */}
        {foodActive.length >= 2 && (
          <polygon points={foodPoly} fill="rgba(212, 118, 10, 0.20)" stroke="#d4760a" strokeWidth={2.5} strokeLinejoin="round" />
        )}
        {foodActive.map((x, i) => (
          <circle key={`cd-${i}`} cx={x.point.x} cy={x.point.y} r={hover && hover.side === 'food' && hover.index === i ? 6 : 4}
            fill="#d4760a" stroke="white" strokeWidth={1.5} />
        ))}

        {/* WINE polygon — drawn only with at least 2 active vertices */}
        {wineActive.length >= 2 && (
          <polygon points={winePoly} fill="rgba(114, 47, 55, 0.20)" stroke="#722f37" strokeWidth={2.5} strokeLinejoin="round" />
        )}
        {wineActive.map((x, i) => (
          <circle key={`vd-${i}`} cx={x.point.x} cy={x.point.y} r={hover && hover.side === 'wine' && hover.index === i ? 6 : 4}
            fill="#722f37" stroke="white" strokeWidth={1.5} />
        ))}

        {/* Hover/tap areas of the plotted points + the explanation of the hovered one (removed from the chart when the pointer leaves) */}
        {[['food', foodActive], ['wine', wineActive]].map(([side, list]) => list.map((x, i) => (
          <circle key={`hit-${side}-${i}`} cx={x.point.x} cy={x.point.y} r={12} fill="transparent" style={{ cursor: 'pointer' }}
            tabIndex={0} role="img" aria-label={pointLines(x).join(', ')}
            onMouseEnter={() => setHover({ side, index: i })} onMouseLeave={() => setHover(null)}
            onFocus={() => setHover({ side, index: i })} onBlur={() => setHover(null)}
            onClick={() => setHover((h) => (h && h.side === side && h.index === i ? null : { side, index: i }))} />
        )))}
        {hoverBox}

        <circle cx={CX} cy={CY} r={2} fill="#666" />

        {/* Footer: structure and pairing boxes */}
        <line x1={20} y1={H - 108} x2={W - 20} y2={H - 108} stroke="#ccc" strokeWidth={0.5} />
        <text x={25} y={H - 84} fontSize={fit(up(t('pairing.chart.food_structure')), 155, 10, 0.72)} fontWeight="700" fill="#000">{up(t('pairing.chart.food_structure'))}</text>
        {['struct_low', 'struct_mid', 'struct_high'].map((k, i) => {
          const bx = 190 + i * 128;
          const txt = t(`pairing.chart.${k}`);
          const lev = !isFilledVal(data.food_structure) ? -1 : Number(data.food_structure) <= 3 ? 0 : Number(data.food_structure) <= 6 ? 1 : 2;
          return (<g key={`sc-${i}`}><text x={bx + 14} y={H - 84} fontSize={fit(txt, 110, 8.5, 0.52)} fill="#555">{txt}</text>
            <rect x={bx} y={H - 93} width={10} height={10} fill={lev === i ? '#d4760a' : 'white'} stroke="#888" strokeWidth={0.6} rx={1} /></g>);
        })}
        <text x={25} y={H - 60} fontSize={fit(up(t('pairing.chart.wine_body')), 155, 10, 0.72)} fontWeight="700" fill="#000">{up(t('pairing.chart.wine_body'))}</text>
        {['struct_low', 'struct_mid', 'struct_high'].map((k, i) => {
          const bx = 190 + i * 128;
          const txt = t(`pairing.chart.${k}`);
          const lev = !isFilledVal(data.wine_body) ? -1 : Number(data.wine_body) <= 3 ? 0 : Number(data.wine_body) <= 6 ? 1 : 2;
          return (<g key={`cv-${i}`}><text x={bx + 14} y={H - 60} fontSize={fit(txt, 110, 8.5, 0.52)} fill="#555">{txt}</text>
            <rect x={bx} y={H - 69} width={10} height={10} fill={lev === i ? '#722f37' : 'white'} stroke="#888" strokeWidth={0.6} rx={1} /></g>);
        })}
        <line x1={20} y1={H - 38} x2={W - 20} y2={H - 38} stroke="#333" strokeWidth={1} />
        <text x={25} y={H - 16} fontSize={fit(up(t('pairing.chart.pairing')), 155, 11, 0.75)} fontWeight="800" fill="#000">{up(t('pairing.chart.pairing'))}</text>
        {['slightly_harmonious', 'fairly_harmonious', 'harmonious'].map((code, i) => {
          const bx = 190 + i * 128;
          const txt = up(tc('outcome', code));
          const match = data.pairing_outcome === code;
          return (<g key={`ab-${i}`}><text x={bx + 14} y={H - 16} fontSize={fit(txt, 110, 8.5, 0.68)} fill="#333" fontWeight="600">{txt}</text>
            <rect x={bx} y={H - 25} width={10} height={10} fill={match ? '#722f37' : 'white'} stroke="#333" strokeWidth={0.7} rx={1} /></g>);
        })}
      </svg>
    </div>
  );
}

const AXES = [
  { key: 'food_sweet_tendency', side: 'food' },
  { key: 'food_fatness', side: 'food' },
  { key: 'food_succulence', side: 'food' },
  { key: 'food_unctuousness', side: 'food' },
  { key: 'food_sapidity', side: 'food' },
  { key: 'food_bitter_tendency', side: 'food' },
  { key: 'food_acid_tendency', side: 'food' },
  { key: 'food_sweetness', side: 'food' },
  { key: 'food_spiciness', side: 'food' },
  { key: 'food_aromaticity', side: 'food' },
  { key: 'food_persistence', side: 'food' },
  { key: 'food_structure', side: 'food' },
  { key: 'wine_alcohol', side: 'wine' },
  { key: 'wine_tannicity', side: 'wine' },
  { key: 'wine_acidity', side: 'wine' },
  { key: 'wine_effervescence', side: 'wine' },
  { key: 'wine_sapidity', side: 'wine' },
  { key: 'wine_sweetness', side: 'wine' },
  { key: 'wine_softness', side: 'wine' },
  { key: 'wine_olfactory_intensity', side: 'wine' },
  { key: 'wine_body', side: 'wine' },
  { key: 'wine_pai', side: 'wine' },
];

export default RadarChart;
export { AXES, isFilledVal };
